(() => {
  const SCRIPT_CACHE = new Map();

  function loadScript(src) {
    if (SCRIPT_CACHE.has(src)) return SCRIPT_CACHE.get(src);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });

    SCRIPT_CACHE.set(src, promise);
    return promise;
  }

  function resolvePdfUrl(pdfUrl) {
    if (!pdfUrl) return pdfUrl;

    try {
      const url = new URL(pdfUrl, window.location.origin);
      const filename = url.pathname.split('/').pop() || '';

      if (filename === 'standard-chucking-mechanisms.pdf' && !url.searchParams.has('v')) {
        url.searchParams.set('v', '1782072400');
      }

      return url.toString();
    } catch {
      return pdfUrl;
    }
  }

  function getBrochureDisplayWidth(isSinglePageDoc = false) {
    const isMobile = window.innerWidth < 750;

    if (!isMobile) {
      return isSinglePageDoc ? Math.min(900, window.innerWidth - 64) : 550;
    }

    return Math.min(isSinglePageDoc ? 340 : 320, window.innerWidth - 32);
  }

  function getPdfRenderScale(page, isSinglePageDoc = false) {
    const displayWidth = getBrochureDisplayWidth(isSinglePageDoc);
    const baseViewport = page.getViewport({ scale: 1 });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 3);
    const renderMultiplier = Math.max(pixelRatio * 2.5, isSinglePageDoc ? 4 : 3);
    const maxRenderWidth = isSinglePageDoc ? 3600 : 2800;
    const renderWidth = Math.min(displayWidth * renderMultiplier, maxRenderWidth);
    let scale = renderWidth / baseViewport.width;
    const maxDim = isSinglePageDoc ? 4096 : 3200;
    const longest = Math.max(baseViewport.width, baseViewport.height) * scale;

    if (longest > maxDim) {
      scale *= maxDim / longest;
    }

    return scale;
  }

  function canvasToImageData(canvas, isSinglePageDoc) {
    if (isSinglePageDoc) {
      return canvas.toDataURL('image/png');
    }

    return canvas.toDataURL('image/jpeg', 0.98);
  }

  function getFlipbookPixelRatio() {
    return Math.min(Math.max(window.devicePixelRatio || 1, 2) * 2, 4);
  }

  function enableHiDpiPageFlip(flipbook) {
    const ui = flipbook?.ui;
    if (!ui?.canvas || typeof ui.resizeCanvas !== 'function' || ui._hiDpiPatched) return;

    ui._hiDpiPatched = true;

    const pixelRatio = getFlipbookPixelRatio();

    ui.resizeCanvas = function resizeCanvasHiDpi() {
      const style = getComputedStyle(this.canvas);
      const cssWidth = parseInt(style.getPropertyValue('width'), 10) || this.canvas.clientWidth;
      const cssHeight = parseInt(style.getPropertyValue('height'), 10) || this.canvas.clientHeight;
      if (!cssWidth || !cssHeight) return;

      this.canvas.width = Math.round(cssWidth * pixelRatio);
      this.canvas.height = Math.round(cssHeight * pixelRatio);

      const ctx = this.canvas.getContext('2d');
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const render = this.app?.getRender?.() || this.app?.render;
      if (render) {
        render.ctx = ctx;
      }
    };

    ui.resizeCanvas();
    flipbook.render?.update?.();
    flipbook.update?.();
  }

  async function renderPdfToImages(pdfUrl) {
    await loadScript('https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js');

    const pdfjsLib = window.pdfjsLib;
    if (!pdfjsLib) throw new Error('PDF.js failed to load');

    pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

    const resolvedPdfUrl = resolvePdfUrl(pdfUrl);
    const pdf = await pdfjsLib.getDocument({ url: resolvedPdfUrl, withCredentials: false }).promise;
    const images = [];
    const isSinglePageDoc = pdf.numPages === 1;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
      const page = await pdf.getPage(pageNum);
      const scale = getPdfRenderScale(page, isSinglePageDoc);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { alpha: false });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      await page.render({ canvasContext: context, viewport, intent: 'print' }).promise;
      images.push(canvasToImageData(canvas, isSinglePageDoc));
    }

    return images;
  }

  function updateBrochureLayout(flipbook, viewer) {
    if (!flipbook || !viewer) return;

    const page = flipbook.getCurrentPageIndex();
    const total = flipbook.getPageCount();
    let mode = 'spread';

    if (total <= 1) {
      mode = 'spread';
    } else if (page === 0) {
      mode = 'cover';
    } else if (page === total - 1) {
      mode = 'back';
    }

    viewer.dataset.brochureMode = mode;

    const modal = viewer.closest('.popup-modal__content');
    if (modal) {
      modal.dataset.brochureMode = mode;
    }

    if (viewer._applyBrochureZoom) {
      requestAnimationFrame(() => viewer._applyBrochureZoom());
    }
  }

  function derivePdfFilename(pdfUrl) {
    try {
      const pathname = new URL(pdfUrl).pathname;
      const raw = pathname.split('/').pop() || 'brochure.pdf';
      const filename = decodeURIComponent(raw.split('?')[0]);
      return filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    } catch {
      return 'brochure.pdf';
    }
  }

  async function downloadPdf(pdfUrl) {
    const response = await fetch(resolvePdfUrl(pdfUrl), { mode: 'cors' });
    if (!response.ok) throw new Error(`Download failed (${response.status})`);

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = derivePdfFilename(pdfUrl);
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  }

  function bindDownloadTriggers(root = document) {
    root.querySelectorAll('[data-brochure-download]').forEach((trigger) => {
      if (trigger.dataset.brochureDownloadBound === 'true') return;
      trigger.dataset.brochureDownloadBound = 'true';

      trigger.addEventListener('click', async (event) => {
        event.preventDefault();

        const pdfUrl = trigger.getAttribute('data-brochure-url') || trigger.getAttribute('href');
        if (!pdfUrl) return;

        if (window.SpeedgripAuth && !window.SpeedgripAuth.loggedIn) {
          window.SpeedgripAuth.gateDownload(pdfUrl);
          return;
        }

        trigger.setAttribute('aria-busy', 'true');

        try {
          await downloadPdf(pdfUrl);
        } catch (error) {
          console.error('Brochure download error:', error);
          window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        } finally {
          trigger.removeAttribute('aria-busy');
        }
      });
    });
  }

  function mountSinglePage(viewer, imageSrc) {
    const frame = document.createElement('div');
    frame.className = 'product-brochure-flipbook__single';

    const img = document.createElement('img');
    img.className = 'product-brochure-flipbook__single-image';
    img.src = imageSrc;
    img.alt = 'Brochure';
    img.loading = 'eager';

    frame.appendChild(img);
    viewer.replaceChildren(frame);
    viewer.dataset.brochureMode = 'single';

    const modal = viewer.closest('.popup-modal__content');
    if (modal) {
      modal.dataset.brochureMode = 'single';
    }

    return null;
  }

  function mountMobilePager(viewer, images) {
    let index = 0;
    let touchStartX = 0;

    viewer.dataset.brochureMode = 'mobile';

    const modal = viewer.closest('.popup-modal__content');
    if (modal) {
      modal.dataset.brochureMode = 'mobile';
    }

    const shell = document.createElement('div');
    shell.className = 'product-brochure-flipbook__mobile';

    const stage = document.createElement('div');
    stage.className = 'product-brochure-flipbook__mobile-stage';

    const img = document.createElement('img');
    img.className = 'product-brochure-flipbook__mobile-image';
    img.alt = 'Brochure page';
    img.loading = 'eager';

    const controls = document.createElement('div');
    controls.className = 'product-brochure-flipbook__mobile-controls';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'product-brochure-flipbook__mobile-nav product-brochure-flipbook__mobile-nav--prev';
    prev.setAttribute('aria-label', 'Previous page');
    prev.innerHTML = '&lsaquo;';

    const counter = document.createElement('p');
    counter.className = 'product-brochure-flipbook__mobile-counter';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'product-brochure-flipbook__mobile-nav product-brochure-flipbook__mobile-nav--next';
    next.setAttribute('aria-label', 'Next page');
    next.innerHTML = '&rsaquo;';

    const render = () => {
      img.src = images[index];
      counter.textContent = `${index + 1} / ${images.length}`;
      prev.disabled = index === 0;
      next.disabled = index === images.length - 1;
    };

    prev.addEventListener('click', () => {
      if (index > 0) {
        index -= 1;
        render();
      }
    });

    next.addEventListener('click', () => {
      if (index < images.length - 1) {
        index += 1;
        render();
      }
    });

    stage.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0]?.screenX || 0;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0]?.screenX || 0;
        const deltaX = touchEndX - touchStartX;

        if (Math.abs(deltaX) < 40) return;
        if (viewer.querySelector('.product-brochure-flipbook__zoom-viewport.is-zoomed')) return;

        if (deltaX < 0 && index < images.length - 1) {
          index += 1;
          render();
        } else if (deltaX > 0 && index > 0) {
          index -= 1;
          render();
        }
      },
      { passive: true }
    );

    stage.appendChild(img);
    controls.appendChild(prev);
    controls.appendChild(counter);
    controls.appendChild(next);
    shell.appendChild(stage);
    shell.appendChild(controls);
    viewer.replaceChildren(shell);
    render();

    return null;
  }

  async function mountFlipbook(viewer, images) {
    await loadScript('https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js');

    const PageFlip = window.St?.PageFlip;
    if (!PageFlip) throw new Error('PageFlip failed to load');

    const book = document.createElement('div');
    book.className = 'product-brochure-flipbook__book';

    const host = document.createElement('div');
    host.className = 'product-brochure-flipbook__host';
    book.appendChild(host);
    viewer.replaceChildren(book);

    const isMobile = window.innerWidth < 750;
    const displayWidth = getBrochureDisplayWidth(false);
    const displayHeight = Math.round(displayWidth * (733 / 550));
    const spreadWidth = isMobile
      ? displayWidth
      : Math.min(1100, window.innerWidth - 32);
    const isSinglePage = images.length <= 1;

    const flipbook = new PageFlip(host, {
      width: isMobile ? displayWidth : 550,
      height: isMobile ? displayHeight : 733,
      size: 'fixed',
      minWidth: isMobile ? displayWidth : 315,
      maxWidth: spreadWidth,
      minHeight: isMobile ? displayHeight : 400,
      maxHeight: isMobile
        ? Math.min(displayHeight + 20, window.innerHeight - 100)
        : Math.min(900, window.innerHeight - 64),
      showCover: !isSinglePage,
      mobileScrollSupport: true,
      usePortrait: isMobile,
    });

    const syncLayout = () => updateBrochureLayout(flipbook, viewer);

    flipbook.on('init', () => {
      enableHiDpiPageFlip(flipbook);
      syncLayout();
    });
    flipbook.on('flip', syncLayout);

    flipbook.loadFromImages(images);
    enableHiDpiPageFlip(flipbook);
    requestAnimationFrame(syncLayout);

    return flipbook;
  }

  window.ProductBrochureFlipbook = {
    async open(viewer, pdfUrl) {
      if (!viewer || !pdfUrl) return;

      this.destroy(viewer);
      viewer.dataset.loading = 'true';
      viewer.innerHTML = '<p class="product-brochure-flipbook__loading">Loading brochure&hellip;</p>';

      try {
        const images = await renderPdfToImages(pdfUrl);
        const isMobile = window.innerWidth < 750;
        viewer.dataset.loading = 'false';
        viewer._flipbookInstance =
          images.length === 1
            ? mountSinglePage(viewer, images[0])
            : isMobile
              ? mountMobilePager(viewer, images)
              : await mountFlipbook(viewer, images);
        attachZoom(viewer);
      } catch (error) {
        viewer.dataset.loading = 'false';
        viewer.innerHTML =
          `<p class="product-brochure-flipbook__error">Unable to load brochure. <a href="${pdfUrl}" target="_blank" rel="noopener">Open PDF</a></p>`;
        console.error('Brochure flipbook error:', error);
      }
    },

    destroy(viewer) {
      if (!viewer) return;

      viewer._zoomCleanup?.();

      if (viewer._flipbookInstance?.destroy) {
        viewer._flipbookInstance.destroy();
      }

      viewer._flipbookInstance = null;
      viewer.replaceChildren();
      delete viewer.dataset.loading;
      delete viewer.dataset.brochureMode;

      const modal = viewer.closest('.popup-modal__content');
      if (modal) {
        delete modal.dataset.brochureMode;
      }
    },

    bindDownloadTriggers,
  }

  const ZOOM_MIN = 1;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;

  function clampZoom(value, snap = true) {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
    if (!snap) return clamped;
    return Math.round(clamped / ZOOM_STEP) * ZOOM_STEP;
  }

  function findZoomTarget(viewer) {
    return (
      viewer.querySelector('.product-brochure-flipbook__book') ||
      viewer.querySelector('.product-brochure-flipbook__single') ||
      viewer.querySelector('.product-brochure-flipbook__mobile-stage') ||
      viewer.firstElementChild
    );
  }

  function ensureZoomControls(lightbox) {
    const actions = lightbox?.querySelector('.product-brochure-flipbook__actions');
    if (!actions) return null;

    let group = actions.querySelector('[data-brochure-zoom]');
    if (group) return group;

    group = document.createElement('div');
    group.className = 'product-brochure-flipbook__zoom';
    group.dataset.brochureZoom = 'true';
    group.innerHTML =
      '<button type="button" class="product-brochure-flipbook__zoom-btn" data-brochure-zoom-in aria-label="Zoom in" title="Zoom in">+</button>' +
      '<span class="product-brochure-flipbook__zoom-label" data-brochure-zoom-label>100%</span>' +
      '<button type="button" class="product-brochure-flipbook__zoom-btn" data-brochure-zoom-out aria-label="Zoom out" title="Zoom out">−</button>';
    actions.appendChild(group);
    return group;
  }

  function attachZoom(viewer) {
    if (viewer.querySelector('.product-brochure-flipbook__zoom-viewport')) return;

    const target = findZoomTarget(viewer);
    if (!target || !target.parentNode) return;

    const lightbox = viewer.closest('.product-feature-page__brochure-lightbox');
    const controls = ensureZoomControls(lightbox);
    const parent = target.parentNode;
    const viewport = document.createElement('div');
    viewport.className = 'product-brochure-flipbook__zoom-viewport';
    const sizer = document.createElement('div');
    sizer.className = 'product-brochure-flipbook__zoom-sizer';
    const stage = document.createElement('div');
    stage.className = 'product-brochure-flipbook__zoom-stage';

    parent.insertBefore(viewport, target);
    stage.appendChild(target);
    sizer.appendChild(stage);
    viewport.appendChild(sizer);

    let scale = 1;
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    let drag = null;
    const abort = new AbortController();
    const { signal } = abort;

    const zoomOutBtn = controls?.querySelector('[data-brochure-zoom-out]');
    const zoomInBtn = controls?.querySelector('[data-brochure-zoom-in]');
    const zoomLabel = controls?.querySelector('[data-brochure-zoom-label]');

    function updateControls() {
      if (zoomLabel) zoomLabel.textContent = `${Math.round(scale * 100)}%`;
      if (zoomOutBtn) zoomOutBtn.disabled = scale <= ZOOM_MIN;
      if (zoomInBtn) zoomInBtn.disabled = scale >= ZOOM_MAX;
      viewport.classList.toggle('is-zoomed', scale > 1);
    }

    function applyLayout() {
      const width = target.offsetWidth;
      const height = target.offsetHeight;
      if (!width || !height) return;

      sizer.style.width = `${width * scale}px`;
      sizer.style.height = `${height * scale}px`;
      stage.style.transform = `scale(${scale})`;
      updateControls();
    }

    function setScale(nextScale, origin, snap = true) {
      const previous = scale || 1;
      const next = clampZoom(nextScale, snap);
      if (Math.abs(next - previous) < 0.001 && origin == null) {
        applyLayout();
        return;
      }

      const rect = viewport.getBoundingClientRect();
      const originX = origin ? origin.x - rect.left : viewport.clientWidth / 2;
      const originY = origin ? origin.y - rect.top : viewport.clientHeight / 2;
      const contentX = (viewport.scrollLeft + originX) / previous;
      const contentY = (viewport.scrollTop + originY) / previous;

      scale = next;
      applyLayout();

      viewport.scrollLeft = contentX * scale - originX;
      viewport.scrollTop = contentY * scale - originY;
    }

    viewer._applyBrochureZoom = applyLayout;
    updateControls();
    requestAnimationFrame(applyLayout);

    zoomInBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      setScale(scale + ZOOM_STEP);
    }, { signal });

    zoomOutBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      setScale(scale - ZOOM_STEP);
    }, { signal });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!(event.ctrlKey || event.metaKey)) return;
        event.preventDefault();
        setScale(scale + (event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP), {
          x: event.clientX,
          y: event.clientY,
        });
      },
      { passive: false, signal }
    );

    viewport.addEventListener(
      'dblclick',
      (event) => {
        if (event.target.closest('button, a')) return;
        event.preventDefault();
        setScale(scale > 1 ? 1 : 2, { x: event.clientX, y: event.clientY });
      },
      { signal }
    );

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (scale <= 1 || event.button !== 0) return;
        if (event.target.closest('button, a')) return;
        drag = {
          x: event.clientX,
          y: event.clientY,
          scrollLeft: viewport.scrollLeft,
          scrollTop: viewport.scrollTop,
        };
        viewport.classList.add('is-panning');
      },
      { signal }
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (!drag) return;
        viewport.scrollLeft = drag.scrollLeft - (event.clientX - drag.x);
        viewport.scrollTop = drag.scrollTop - (event.clientY - drag.y);
      },
      { signal }
    );

    window.addEventListener(
      'pointerup',
      () => {
        drag = null;
        viewport.classList.remove('is-panning');
      },
      { signal }
    );

    viewport.addEventListener(
      'mousedown',
      (event) => {
        if (scale > 1) event.stopPropagation();
      },
      { capture: true, signal }
    );

    viewport.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length === 2) {
          pinchStartDistance = Math.hypot(
            event.touches[0].clientX - event.touches[1].clientX,
            event.touches[0].clientY - event.touches[1].clientY
          );
          pinchStartScale = scale;
        }
      },
      { passive: true, signal }
    );

    viewport.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 2 || !pinchStartDistance) return;
        event.preventDefault();
        const distance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        );
        setScale(pinchStartScale * (distance / pinchStartDistance), {
          x: (event.touches[0].clientX + event.touches[1].clientX) / 2,
          y: (event.touches[0].clientY + event.touches[1].clientY) / 2,
        }, false);
      },
      { passive: false, signal }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        pinchStartDistance = 0;
      },
      { passive: true, signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.target.closest('input, textarea, select, [contenteditable]')) return;
        if (lightbox && !lightbox.hasAttribute('open')) return;

        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          setScale(scale + ZOOM_STEP);
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          setScale(scale - ZOOM_STEP);
        } else if (event.key === '0') {
          event.preventDefault();
          setScale(1);
        }
      },
      { signal }
    );

    viewer._zoomCleanup = () => {
      abort.abort();
      scale = 1;
      updateControls();
      delete viewer._applyBrochureZoom;
      delete viewer._zoomCleanup;
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindDownloadTriggers());
  } else {
    bindDownloadTriggers();
  }
})();
